import { useEffect, useRef, useState } from "react";
import { api, handleSessionExpired, performLogout, UnauthorizedError } from "@/api/client";

// 경고 다이얼로그의 "로그아웃" — 반드시 서버 세션을 파기한 뒤 로그인 화면으로 보낸다.
// (과거: window.location.href="/" 만 호출 → 세션/쿠키가 살아있어 로그아웃이 되지 않고,
//  SSO(IdP) 세션이 유효한 채 /login 을 타 즉시 재인가되어 "다시 로그인" 되던 버그)
// pilgrims 모드에서는 공유 쿠키(pilgrims_auth)까지 만료시켜야 완전 로그아웃된다 → performLogout.
const doLogout = () => performLogout();

const IDLE_LIMIT = 30 * 60 * 1000; // 세션 idle 만료(서버 30분)
const WARN_AT = 27 * 60 * 1000; // 만료 3분 전 경고
const RENEW_EVERY = 10 * 60 * 1000; // 활성 사용자 자동 갱신 주기
// 탭/창 간 마지막 활동 공유 키 — idle 판정은 "모든 창 중 가장 최근 활동" 기준.
// (창 단위로만 재던 과거엔 잊힌 백그라운드 창이 30분 뒤 performLogout 으로 서버
//  세션·pilgrims 쿠키를 전역 파기 → 사용 중인 창이 갑자기 새로고침되던 버그)
const ACTIVITY_KEY = "arkiveai.lastActivity";

/** 세션 만료 사전 경고 + 활성 시 자동 갱신. */
export default function SessionGuard() {
  const [warn, setWarn] = useState(false);
  // 인증 비활성(auth_mode==="disabled") 모드 여부 — true 면 세션 개념이 없으므로
  // idle 경고/자동 로그오프를 전부 끈다(다이얼로그가 떠도 로그아웃이 불가능한 상태였음).
  const [enabled, setEnabled] = useState(false);
  const lastActivity = useRef(Date.now());
  const lastShared = useRef(0); // localStorage 공유 쓰기 스로틀(5초)
  const lastRenew = useRef(Date.now());
  const loggingOut = useRef(false); // idle 만료 로그아웃 1회만 실행

  useEffect(() => {
    let mounted = true;
    api
      .authConfig()
      .then((cfg) => {
        if (mounted) setEnabled(cfg.auth_mode !== "disabled");
      })
      .catch(() => {
        if (mounted) setEnabled(true); // config 조회 실패 — 안전하게 가드 활성
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const onActivity = () => {
      const now = Date.now();
      lastActivity.current = now;
      // 다른 탭/PWA 창의 idle 판정에도 반영되도록 공유(쓰기 부하를 줄이려 5초 스로틀)
      if (now - lastShared.current > 5000) {
        lastShared.current = now;
        try {
          localStorage.setItem(ACTIVITY_KEY, String(now));
        } catch {
          /* ignore */
        }
      }
    };
    // 읽기(스크롤/휠/터치)도 활동으로 인정 — 답변을 읽는 동안 idle 로그아웃되지 않도록.
    // ⚠ visibilitychange 는 여기서 제외한다. 탭을 떠났다 돌아오는 것만으로 idle 타이머가
    //   리셋되면, 서버 세션은 이미 만료됐는데 클라이언트는 "활성"으로 오인해 화면을 계속
    //   띄운다(= 화면은 보이는데 기능만 깨지는 문제). 복귀 시점엔 리셋 대신 세션을 검증한다.
    const events = ["pointerdown", "keydown", "wheel", "scroll", "touchstart"];
    events.forEach((e) =>
      window.addEventListener(e, onActivity, { passive: true, capture: true }),
    );

    // 서버 세션 검증 → 만료면 즉시 자동 로그오프. 살아있으면 슬라이드 타이밍 갱신.
    const verifySession = async () => {
      try {
        await api.me();
        lastRenew.current = Date.now();
        // me() 성공 = 서버가 세션 유효 확인 + 슬라이딩(+30분). 서버가 기준이므로
        // 로컬 idle 타이머도 함께 리셋한다. 리셋하지 않으면 장시간 자리 비움 후
        // 복귀했을 때, 검증으로 세션이 막 살아났는데도 30초 내 인터벌이 stale idle
        // 로 performLogout 을 걸어 "돌아오자마자 저절로 새로고침"되던 버그가 난다.
        lastActivity.current = Date.now();
      } catch (e) {
        if (e instanceof UnauthorizedError) handleSessionExpired(); // 만료 → 로그인 전환
        /* 그 외(네트워크 등)는 다음 주기/요청에서 처리 */
      }
    };

    // 탭 복귀 시: idle 타이머를 리셋하지 않고 서버 세션이 살아있는지 즉시 확인한다.
    const onVisible = () => {
      if (document.visibilityState === "visible") verifySession();
    };
    window.addEventListener("visibilitychange", onVisible);

    const iv = setInterval(() => {
      // 다른 탭/창의 활동도 idle 판정에 포함(전역 로그아웃 경합 방지)
      let last = lastActivity.current;
      try {
        const shared = Number(localStorage.getItem(ACTIVITY_KEY));
        if (shared > last) last = shared;
      } catch {
        /* ignore */
      }
      const idle = Date.now() - last;
      if (idle >= IDLE_LIMIT) {
        // 숨은(백그라운드) 창에서는 로그아웃하지 않는다 — 서버 세션은 자연 만료되고,
        // 복귀 시 verifySession 이 서버 기준으로 판정한다. 백그라운드 창이
        // performLogout 을 실행하면 사용 중인 다른 창까지 전역 로그아웃된다.
        if (document.visibilityState !== "visible") return;
        // 만료 → 완전 로그아웃. handleSessionExpired(reload)는 pilgrims 모드에서
        // 공유 쿠키 부트스트랩으로 즉시 재로그인되어 로그오프가 되지 않는다.
        // performLogout 은 pilgrims logout_url 로 이동해 공유 쿠키까지 만료시킨다.
        if (!loggingOut.current) {
          loggingOut.current = true;
          performLogout();
        }
        return;
      }
      if (idle >= WARN_AT) {
        setWarn(true);
        return;
      }
      setWarn(false);
      // 활성 사용자(최근 5분 내 활동, 화면 보임) → 주기적으로 세션 슬라이드 겸 검증
      if (
        document.visibilityState === "visible" &&
        idle < 5 * 60 * 1000 &&
        Date.now() - lastRenew.current >= RENEW_EVERY
      ) {
        verifySession();
      }
    }, 30 * 1000);

    return () => {
      events.forEach((e) =>
        window.removeEventListener(e, onActivity, { capture: true }),
      );
      window.removeEventListener("visibilitychange", onVisible);
      clearInterval(iv);
    };
  }, [enabled]);

  const extend = async () => {
    try {
      await api.me();
    } catch {
      /* ignore */
    }
    lastActivity.current = Date.now();
    lastRenew.current = Date.now();
    try {
      localStorage.setItem(ACTIVITY_KEY, String(Date.now())); // 다른 창에도 연장 전파
    } catch {
      /* ignore */
    }
    setWarn(false);
  };

  if (!enabled || !warn) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xs rounded-xl bg-card p-4 text-center shadow-xl">
        <p className="text-sm font-medium">세션이 곧 만료됩니다</p>
        <p className="mt-1 text-xs text-muted-foreground">계속 이용하시려면 연장해 주세요.</p>
        <div className="mt-3 flex gap-2">
          <button
            onClick={extend}
            className="flex-1 rounded-lg bg-[#0E1E2D] hover:bg-[#1C3954] py-2 text-sm font-semibold text-white"
          >
            계속하기
          </button>
          <button
            onClick={doLogout}
            className="rounded-lg border border-border px-3 py-2 text-sm"
          >
            로그아웃
          </button>
        </div>
      </div>
    </div>
  );
}
